package com.arenaproplay.admin.ui.components.charts.svg

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div

/**
 * Standalone Night Arena sparkline — gradient fill + Catmull-Rom smooth stroke +
 * terminal dot with dark stroke. Replaces/upgrades the basic linear sparkline in
 * KpiCard. Ported from kontaq sparklineSvg (SimpleTabs.kt).
 *
 * @param values    data points (empty or single-point → subtle stub, no crash)
 * @param colorHex  stroke/fill accent color, e.g. "#0061FF"
 * @param width     pixel width of the SVG element
 * @param height    pixel height of the SVG element
 * @param modifier  standard Kobweb modifier
 */
@Composable
fun SparklineSvg(
    values: List<Double>,
    colorHex: String = "#0061FF",
    width: Int = 160,
    height: Int = 40,
    modifier: Modifier = Modifier,
) {
    val svg = buildSparklineSvg(values, colorHex, width, height)
    // M1: 1-point/empty → show the rgba(255,255,255,.02) stub (same as AreaChart/BarColumns)
    if (svg.isEmpty()) {
        Div(attrs = modifier.toAttrs {
            style {
                property("width", "${width}px")
                property("height", "${height}px")
                property("background", "rgba(255,255,255,.02)")
                property("border-radius", "8px")
                property("flex-shrink", "0")
            }
        })
        return
    }
    Div(
        attrs = modifier.toAttrs {
            style {
                property("width", "${width}px")
                property("height", "${height}px")
                property("line-height", "0")
                property("flex-shrink", "0")
            }
            ref { el ->
                el.innerHTML = svg
                onDispose { el.innerHTML = "" }
            }
        }
    )
}

// ── SVG string builder ─────────────────────────────────────────────────────

internal fun buildSparklineSvg(
    values: List<Double>,
    colorHex: String,
    w: Int,
    h: Int,
): String {
    val safe = values.filter { !it.isNaN() && !it.isInfinite() }
    if (safe.size < 2) return ""

    val padY = 5.0
    val n = safe.size
    val minV = safe.min()
    val maxV = safe.max()
    val range = (maxV - minV).coerceAtLeast(0.0001)

    val px = DoubleArray(n) { i -> w.toDouble() * i / (n - 1) }
    val py = DoubleArray(n) { i -> padY + (h - 2 * padY) * (1.0 - (safe[i] - minV) / range) }

    val line = smoothPath(px, py)
    val area = "$line L ${fmt(px[n - 1])} ${fmt(h.toDouble())} L ${fmt(px[0])} ${fmt(h.toDouble())} Z"

    // Unique-ish gradient ID (no JS random — use value sum as seed)
    val gradId = "nask${kotlin.math.abs(values.hashCode() * 31 + colorHex.hashCode())}"

    return buildString {
        append("<svg viewBox=\"0 0 $w $h\" width=\"$w\" height=\"$h\"")
        append(" xmlns=\"http://www.w3.org/2000/svg\" style=\"display:block\">")
        append("<defs>")
        append("<linearGradient id=\"$gradId\" x1=\"0\" y1=\"0\" x2=\"0\" y2=\"1\">")
        append("<stop offset=\"0\" stop-color=\"$colorHex\" stop-opacity=\"0.40\"/>")
        append("<stop offset=\"1\" stop-color=\"$colorHex\" stop-opacity=\"0\"/>")
        append("</linearGradient>")
        append("</defs>")
        append("<path d=\"$area\" fill=\"url(#$gradId)\"/>")
        append("<path d=\"$line\" fill=\"none\" stroke=\"$colorHex\"")
        append(" stroke-width=\"2.5\" stroke-linecap=\"round\" stroke-linejoin=\"round\"/>")
        // Terminal dot — dark stroke for contrast on any background
        val lx = fmt(px[n - 1])
        val ly = fmt(py[n - 1])
        append("<circle cx=\"$lx\" cy=\"$ly\" r=\"3.5\"")
        append(" fill=\"$colorHex\" stroke=\"#05080F\" stroke-width=\"2\"/>")
        append("</svg>")
    }
}
